import React, { useRef, useState, useCallback, useEffect, useLayoutEffect, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import followTrigger from '@aytm/app/stats/components/overlay-panel/lib/followTrigger'

import { OverlayPanel as PrimereactOverlayPanel } from 'primereact/overlaypanel'

import style from '@aytm/app/stats/components/overlay-panel/styles/OverlayPanel.module.scss'

const OPTIONS = '.p-listbox-item:not(.p-disabled)'

// The option the keyboard points at carries this mark, which the menu styles paint as their hover
export const KEY_ACTIVE = 'data-key-active'

// Where Down or Up lands: the next or previous option, held at the ends. With nothing marked yet the
// walk starts from the chosen option, so the first press already moves off it -- marking the value
// the menu already holds would be a press that shows no change. With nothing chosen either, the
// first (Down) or the last (Up).
export const nextOption = (options, active, step) => {
  if (!options.length) return null

  const marked = options.indexOf(active)
  const from = -1 !== marked ? marked : options.findIndex(option => option.classList.contains('p-highlight'))
  if (-1 !== from) return options[Math.min(Math.max(from + step, 0), options.length - 1)]

  return 0 < step ? options[0] : options.at(-1)
}

const OverlayPanel = React.forwardRef(
  ({ children, button, testId = 'stat-overlay-panel-button', panelClassName, appendTo, onShow, onHide }, ref) => {
    const [opened, setOpened] = useState(false)
    const [buttonWidth, setButtonWidth] = useState(0)

    const panelRef = useRef(null)
    const buttonRef = useRef(null)
    const containerRef = useRef(null)

    // A hide takes a moment to play out, and the caller's own re-render moves the trigger while it
    // does -- so the realign below would arrive mid-hide and re-open the panel, since the overlay
    // reads a request to align a hidden panel as a request to show it.
    const hidingRef = useRef(false)

    // Focus stays on the trigger while the keyboard walks the list, so the option it points at is kept here
    const activeRef = useRef(null)

    // The panel is as wide as the trigger it hangs off, and a trigger has no laid-out width during
    // render -- so the reading is taken after layout and again whenever the panel opens, in case the
    // trigger has since changed. Only a reading that moves the answer is committed: a panel reopened
    // at the same size re-renders nothing.
    const measureButton = useCallback(() => {
      const width = Math.round(buttonRef.current?.getBoundingClientRect().width ?? 0)

      setButtonWidth(prev => (prev === width ? prev : width))
    }, [])

    // Mounted on the body the panel carries page coordinates written once, so it has to be told to
    // line up again whenever its trigger moves. Re-showing an open panel is the only way in: the
    // overlay treats it as a request to re-align and does nothing else. Mounted beside the trigger
    // there is nothing to do -- the panel moves with it.
    const realign = useCallback(() => {
      // The flag covers only this wrapper's own hide; a toggle from the trigger, or the overlay closing
      // itself on scroll, is seen by the exit the overlay is already playing.
      if (hidingRef.current || panelRef.current?.getElement()?.classList.contains('p-overlaypanel-exit')) return

      measureButton()
      panelRef.current?.show(null, containerRef.current)
    }, [measureButton])

    const markOption = option => {
      activeRef.current?.removeAttribute(KEY_ACTIVE)
      activeRef.current = option ?? null
      option?.setAttribute(KEY_ACTIVE, '')
      option?.scrollIntoView?.({ block: 'nearest' })
    }

    const onButtonKeyDown = event => {
      if ('ArrowDown' === event.key || 'ArrowUp' === event.key) {
        if (!opened) return

        event.preventDefault()
        const options = [...(panelRef.current?.getElement()?.querySelectorAll(OPTIONS) ?? [])]
        markOption(nextOption(options, activeRef.current, 'ArrowDown' === event.key ? 1 : -1))
        return
      }

      if ('Enter' !== event.key && ' ' !== event.key) return

      event.preventDefault()
      // a marked option is picked exactly as a click on it would pick it
      if (opened && activeRef.current?.isConnected) return void activeRef.current.click()

      panelRef.current?.toggle(event, containerRef.current)
    }

    useEffect(() => {
      if (appendTo || !opened) return

      return followTrigger(containerRef.current, realign)
    }, [appendTo, opened, realign])

    // Escape closes the panel from anywhere while it is open and gives focus back to the trigger; focus
    // moving anywhere but the trigger or the panel closes it too
    useEffect(() => {
      if (!opened) return

      const onFocusIn = event => {
        const panel = panelRef.current?.getElement()
        if (buttonRef.current?.contains(event.target) || panel?.contains(event.target)) return

        panelRef.current?.hide()
      }

      const onKeyDown = event => {
        if ('Escape' !== event.key) return

        const panel = panelRef.current?.getElement()
        const hadFocus = panel?.contains(document.activeElement) || buttonRef.current === document.activeElement
        panelRef.current?.hide()
        hadFocus && buttonRef.current?.focus()
      }

      document.addEventListener('keydown', onKeyDown)
      document.addEventListener('focusin', onFocusIn)
      return () => {
        document.removeEventListener('keydown', onKeyDown)
        document.removeEventListener('focusin', onFocusIn)
      }
    }, [opened])

    useLayoutEffect(measureButton, [measureButton, button])

    // Hiding from inside the panel's own click unbinds the outside-click listener before that click
    // reaches it, so the panel keeps its "the click was mine" flag and swallows the NEXT outside
    // click. Deferring the hide lets the listener run once more and clear the flag.
    useImperativeHandle(
      ref,
      () => ({
        toggle: (event, target) => panelRef.current?.toggle(event, target),
        show: (event, target) => panelRef.current?.show(event, target),
        hide: () => {
          hidingRef.current = true
          // a pick made from the list leaves focus on an option about to unmount; the trigger keeps it
          panelRef.current?.getElement()?.contains(document.activeElement) && buttonRef.current?.focus()
          setTimeout(() => panelRef.current?.hide(), 0)
        },
        getElement: () => panelRef.current?.getElement()
      }),
      []
    )

    return (
      <div className="p-overlaypanel-box">
        <div
          ref={buttonRef}
          data-testid={testId}
          data-expanded={opened}
          className={classNames('p-overlaypanel-button', style.button)}
          // a button may say whether its panel is open, which a plain focusable box may not; its name
          // is what it shows (the selected value, or the action's own words)
          role="button"
          aria-expanded={opened}
          tabIndex={0}
          onClick={event => ref.current.toggle(event, containerRef.current)}
          onKeyDown={onButtonKeyDown}
        >
          {button}
        </div>
        <span ref={containerRef} className="p-overlaypanel-container">
          <PrimereactOverlayPanel
            ref={panelRef}
            className={classNames('PRAYTMTheme', style.overlay, panelClassName)}
            appendTo={appendTo ? containerRef.current || 'self' : null}
            showCloseIcon={false}
            style={{ width: `${buttonWidth}px` }}
            // The overlay reports itself shown only once its opening transition has played, while its
            // options are on screen from the first frame -- so the keys follow the transition's own
            // start and end, or a reader quick on the keyboard presses into a panel that ignores them.
            transitionOptions={{ onEnter: () => void setOpened(true), onExit: () => void setOpened(false) }}
            onShow={() => {
              hidingRef.current = false
              measureButton()
              setOpened(true)
              markOption(null)
              onShow?.()
            }}
            onHide={() => {
              hidingRef.current = false
              setOpened(false)
              markOption(null)
              onHide?.()
            }}
          >
            {children}
          </PrimereactOverlayPanel>
        </span>
      </div>
    )
  }
)

OverlayPanel.propTypes = {
  children: PropTypes.any,
  button: PropTypes.any,
  testId: PropTypes.string, // hook for the trigger itself, which is what takes focus and opens the panel; a generic one when none is given
  panelClassName: PropTypes.string, // lets one caller reskin its own popup without moving the shared chrome
  // Where the popup mounts. Left unset it goes to `document.body`; `'self'` keeps it beside the
  // trigger, which is what a caller wants when the popup has to sit inside a scrolling panel.
  appendTo: PropTypes.bool,
  onShow: PropTypes.func,
  onHide: PropTypes.func
}

OverlayPanel.displayName = 'OverlayPanel'

export default OverlayPanel
